import React, { useState, useEffect } from 'react';
import { Activity, Clock, Cpu, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// Role-to-emoji mapping for agent avatars
const ROLE_AVATARS = {
  CAO: '👑',
  CTO: '🔧',
  CMO: '📣',
  CFO: '💰',
  COO: '⚙️',
  WORKER_DEVOPS: '🚀',
  WORKER_EMAIL_OPS: '📧',
  WORKER_SEO: '🔍',
};

const ROLE_LABELS = {
  CAO: 'Chief Agentic Officer',
  CTO: 'Chief Technology Officer',
  CMO: 'Chief Marketing Officer',
  CFO: 'Chief Financial Officer',
  COO: 'Chief Operating Officer',
  WORKER_DEVOPS: 'DevOps Worker',
  WORKER_EMAIL_OPS: 'Email Ops Worker',
  WORKER_SEO: 'SEO Worker',
};

// Model color coding per specification
const MODEL_COLORS = {
  grok: {
    bg: 'bg-purple-900/40',
    border: 'border-purple-500/40',
    badge: 'bg-purple-600/30 text-purple-300 border border-purple-500/30',
    glow: 'shadow-purple-500/20',
    dot: 'bg-purple-400',
  },
  codex: {
    bg: 'bg-green-900/40',
    border: 'border-green-500/40',
    badge: 'bg-green-600/30 text-green-300 border border-green-500/30',
    glow: 'shadow-green-500/20',
    dot: 'bg-green-400',
  },
  groq: {
    bg: 'bg-blue-900/40',
    border: 'border-blue-500/40',
    badge: 'bg-blue-600/30 text-blue-300 border border-blue-500/30',
    glow: 'shadow-blue-500/20',
    dot: 'bg-blue-400',
  },
  kimi: {
    bg: 'bg-orange-900/40',
    border: 'border-orange-500/40',
    badge: 'bg-orange-600/30 text-orange-300 border border-orange-500/30',
    glow: 'shadow-orange-500/20',
    dot: 'bg-orange-400',
  },
  claude: {
    bg: 'bg-violet-900/40',
    border: 'border-violet-500/40',
    badge: 'bg-violet-600/30 text-violet-300 border border-violet-500/30',
    glow: 'shadow-violet-500/20',
    dot: 'bg-violet-400',
  },
  default: {
    bg: 'bg-slate-900/40',
    border: 'border-slate-500/40',
    badge: 'bg-slate-600/30 text-slate-300 border border-slate-500/30',
    glow: 'shadow-slate-500/20',
    dot: 'bg-slate-400',
  },
};

function detectModelFamily(model) {
  if (!model) return 'default';
  const lower = model.toLowerCase();
  if (lower.includes('grok')) return 'grok';
  if (lower.includes('codex')) return 'codex';
  if (lower.includes('groq') || lower.includes('llama')) return 'groq';
  if (lower.includes('kimi')) return 'kimi';
  if (lower.includes('claude') || lower.includes('anthropic') || lower.includes('sonnet') || lower.includes('opus')) return 'claude';
  return 'default';
}

function formatUptime(seconds) {
  if (seconds == null || seconds < 0) return '--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatModelLabel(model) {
  if (!model) return 'Unknown';
  // Shorten for display
  return model.split('/').pop() || model;
}

export default function AgentCard({ agent, onClick, isSelected = false, compact = false }) {
  const [uptimeDisplay, setUptimeDisplay] = useState(formatUptime(agent?.uptime));

  // Tick uptime counter every second when agent is healthy
  useEffect(() => {
    if (!agent || agent.status !== 'healthy') return;
    const startUptime = agent.uptime || 0;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setUptimeDisplay(formatUptime(startUptime + elapsed));
    }, 1000);

    return () => clearInterval(interval);
  }, [agent?.uptime, agent?.status]);

  if (!agent) return null;

  const modelFamily = detectModelFamily(agent.model);
  const colors = MODEL_COLORS[modelFamily] || MODEL_COLORS.default;
  const avatar = ROLE_AVATARS[agent.role] || '🤖';
  const roleLabel = ROLE_LABELS[agent.role] || agent.role;
  const isHealthy = agent.status === 'healthy' || agent.status === 'online';
  const isOffline = agent.status === 'offline' || agent.status === 'error';

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick(agent);
    }
  };

  return (
    <div
      className={`
        relative rounded-xl border transition-all duration-300 cursor-pointer select-none
        ${colors.bg} ${colors.border}
        ${isSelected
          ? `ring-2 ring-[#7c3aed] shadow-lg ${colors.glow}`
          : `hover:ring-1 hover:ring-[#7c3aed]/50 hover:shadow-md hover:shadow-[#7c3aed]/10`
        }
        ${compact ? 'p-3' : 'p-4'}
      `}
      onClick={() => onClick && onClick(agent)}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Agent card: ${agent.name}, ${roleLabel}, status: ${agent.status}`}
      aria-pressed={isSelected}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#7c3aed] animate-pulse" />
      )}

      {/* Header row: avatar + name + status */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`
          flex-shrink-0 flex items-center justify-center rounded-lg
          ${compact ? 'w-9 h-9 text-lg' : 'w-11 h-11 text-2xl'}
          bg-[#0a0e1a] border border-[#7c3aed]/20
        `}>
          {avatar}
        </div>

        {/* Name and title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold text-white truncate ${compact ? 'text-sm' : 'text-base'}`}>
              {agent.name}
            </h3>
          </div>
          <p className={`text-slate-400 truncate ${compact ? 'text-xs' : 'text-xs mt-0.5'}`}>
            {roleLabel}
          </p>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0 flex items-center gap-1.5">
          {isHealthy ? (
            <span className="relative flex items-center gap-1">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              {!compact && (
                <span className="text-xs text-green-400 font-medium">Live</span>
              )}
            </span>
          ) : isOffline ? (
            <span className="flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
              {!compact && (
                <span className="text-xs text-red-400 font-medium">Offline</span>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-500 animate-pulse" />
              {!compact && (
                <span className="text-xs text-yellow-400 font-medium">Checking</span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Model badge */}
      {!compact && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-mono ${colors.badge}`}>
            <Cpu className="w-3 h-3" />
            {formatModelLabel(agent.model)}
          </span>
          {agent.port && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-[#0a0e1a] text-slate-500 border border-slate-700/50">
              :{agent.port}
            </span>
          )}
        </div>
      )}

      {/* Stats row */}
      {!compact && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {/* Uptime */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <span className="truncate font-mono">{uptimeDisplay}</span>
          </div>

          {/* Tasks processed */}
          {agent.tasks_processed != null && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Activity className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <span className="font-mono">{agent.tasks_processed} tasks</span>
            </div>
          )}
        </div>
      )}

      {/* Last task summary */}
      {!compact && agent.last_task && (
        <div className="mt-3 pt-3 border-t border-slate-700/40">
          <p className="text-xs text-slate-500 mb-0.5">Last task</p>
          <p className="text-xs text-slate-300 truncate">{agent.last_task}</p>
        </div>
      )}

      {/* Click hint */}
      {!compact && onClick && (
        <div className="mt-3 pt-2 border-t border-slate-700/40">
          <p className="text-xs text-[#7c3aed]/70 text-center">
            Click to send task
          </p>
        </div>
      )}
    </div>
  );
}
